import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { fieldMapping } from '../config/fieldMapping';

// Removed local supabase initialization as it's now imported from services/supabaseClient


const SalesDataContext = createContext();

export const useSalesData = () => useContext(SalesDataContext);

export const SalesDataProvider = ({ tramiteId, contactId, authFetch, preHydratedData, conversationMetadata, children }) => {
    // Initial state matching sections defined in mapping
    const [sectionData, setSectionData] = useState({
        LeadInfoCard: {},
        SalesForm: {}
    });

    // Track the source of each field in SalesForm: 'manual', 'zoho', 'invoice'
    const [fieldSources, setFieldSources] = useState({});

    // Status state specifically for the form
    const [formStatus, setFormStatus] = useState('DRAFT');

    // Global session-wide state
    const [offers, setOffers] = useState([]);
    const [loadingOffers, setLoadingOffers] = useState(true);
    const [tarifas, setTarifas] = useState([]);
    const [loadingTarifas, setLoadingTarifas] = useState(false);
    const tarifasLoadedRef = useRef(false);
    const tarifasRequestRef = useRef(null);

    // Context-wide state for file extraction and price calculations
    const [priceData, setPriceData] = useState({
        imagina: null,
        comparison: null,
        loadingImagina: false,
        loadingComparison: false,
    });
    const [extractedData, setExtractedData] = useState({});
    const [analyzedAttachments, setAnalyzedAttachments] = useState(new Set());
    const [scoringError, setScoringError] = useState(null);
    const [comparatorError, setComparatorError] = useState(null);

    const distributeData = useCallback((formData) => {
        setSectionData(prev => {
            const updated = { ...prev };
            // Clone sections to prevent mutation
            Object.keys(updated).forEach(k => updated[k] = { ...updated[k] });

            Object.keys(formData).forEach(key => {
                const mappings = fieldMapping[key];
                if (mappings) {
                    mappings.forEach(map => {
                        if (!updated[map.section]) {
                            updated[map.section] = {};
                        }
                        updated[map.section][map.field] = formData[key];
                    });
                }
            });
            return updated;
        });
    }, []);

    // Lead-specific UI state and helpers
    const [leadLoading, setLeadLoading] = useState(false);
    const [leadSearchId, setLeadSearchId] = useState(null);
    const leadSearchRef = useRef(null);

    const updateSectionData = useCallback((section, newData, source = 'manual') => {
        setSectionData(prev => {
            const next = { ...prev };
            // if newData is falsy or an empty object, replace the section entirely
            const isEmptyObject = newData && typeof newData === 'object' && Object.keys(newData).length === 0;
            if (!newData || isEmptyObject) {
                next[section] = {};
            } else {
                next[section] = {
                    ...prev[section],
                    ...newData
                };
            }
            return next;
        });

        // Only track sources for SalesForm
        if (section === 'SalesForm' && newData && typeof newData === 'object') {
            // Track which tramiteId this hydration belongs to
            if (tramiteId) {
                lastHydratedTramiteIdRef.current = tramiteId;
            }

            setFieldSources(prev => {
                const next = { ...prev };
                Object.keys(newData).forEach(key => {
                    next[key] = source;
                });
                return next;
            });
        }
    }, [tramiteId]);

    const lastHydratedTramiteIdRef = useRef(null);

    const fetchLeadByPhone = useCallback(async (phone) => {
        if (!phone) return;
        const searchId = Date.now();
        leadSearchRef.current = searchId;
        setLeadSearchId(searchId);
        setLeadLoading(true);

        try {
            const res = await authFetch(`/api/zoho/lead-info?phone=${encodeURIComponent(phone)}`);
            // If another search started after this one, ignore results
            if (leadSearchRef.current !== searchId) return;
            if (res.ok) {
                const data = await res.json();
                if (leadSearchRef.current !== searchId) return;
                if (data && data.success && data.lead) {
                    updateSectionData('LeadInfoCard', data.lead, 'zoho');
                } else {
                    // Explicitly clear lead info when none found
                    updateSectionData('LeadInfoCard', {});
                }
            } else {
                updateSectionData('LeadInfoCard', {});
            }
        } catch (err) {
            if (leadSearchRef.current !== searchId) return;
            console.error('[SalesDataProvider] fetchLeadByPhone error', err);
            updateSectionData('LeadInfoCard', {});
        } finally {
            // Only clear loading for the latest search
            if (leadSearchRef.current === searchId) {
                setLeadLoading(false);
                // clear search id
                leadSearchRef.current = null;
                setLeadSearchId(null);
            }
        }
    }, [authFetch, updateSectionData]);

    useEffect(() => {
        const isNewSession = lastHydratedTramiteIdRef.current !== tramiteId || (contactId && !tramiteId);

        if (isNewSession) {
            console.log('[SalesDataProvider] 🔄 New session detected, resetting state:', { from: lastHydratedTramiteIdRef.current, to: tramiteId });
            // RESET STATE only on true change
            setSectionData({
                LeadInfoCard: {},
                SalesForm: {}
            });
            setFieldSources({});
            setFormStatus('DRAFT');
            setPriceData({
                imagina: null,
                comparison: null,
                loadingImagina: false,
                loadingComparison: false,
            });
            setExtractedData({});
            setAnalyzedAttachments(new Set());
            setScoringError(null);
            setComparatorError(null);
            lastHydratedTramiteIdRef.current = tramiteId;
        }

        const fetchInitialData = async () => {
            const endpoint = tramiteId 
                ? `/api/sale/data/${tramiteId}` 
                : `/api/sale/data-by-contact/${contactId}`;
            
            console.log('[SalesDataProvider] Fetching initial data from:', endpoint);
            try {
                const res = await authFetch(endpoint);
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.form_data) {
                        distributeData(data.form_data);
                        if (data.status) {
                            setFormStatus(data.status);
                        }
                    }
                }
            } catch (err) {
                console.error("[SalesDataProvider] Fetch error:", err);
            }
        };

        if (tramiteId && tramiteId !== 'loading' && isNewSession) {
            fetchInitialData();
        }

        // Even if it's NOT a new session (e.g. just adding preHydratedData), we distribute it
        if (preHydratedData) {
            console.log('[SalesDataProvider] Hydrating with pre-provided sales data');
            distributeData(preHydratedData);
        }

        // Handle conversation metadata if it arrives later
        if (conversationMetadata && conversationMetadata.attachments) {
            const newExtractedData = {};
            conversationMetadata.attachments.forEach(att => {
                const result = att.metadata?.extraction_result;
                if (result) {
                    newExtractedData[att.attachment_id || att.id] = result;
                }
            });
            if (Object.keys(newExtractedData).length > 0) {
                setExtractedData(prev => ({ ...prev, ...newExtractedData }));
            }
        }
    }, [tramiteId, contactId, authFetch, distributeData, preHydratedData, conversationMetadata]);

    // SESSION-WIDE DATA (Load once per session or when agent changes)
    useEffect(() => {
        if (!authFetch) return;

        // Fetch offers globally so they are available even if the visual section is hidden
        const fetchOffers = async () => {
            try {
                const res = await authFetch('/api/sale/user/offers');
                if (res.ok) {
                    const data = await res.json();
                    setOffers(data.offers || []);
                }
            } catch (err) {
                console.error("DEBUG [SalesDataProvider] Error fetching offers:", err);
            } finally {
                setLoadingOffers(false);
            }
        };

        fetchOffers();
    }, [authFetch]);

    const loadTarifas = useCallback(async () => {
        if (!authFetch || tarifasLoadedRef.current) return;
        if (tarifasRequestRef.current) return tarifasRequestRef.current.promise;

        const request = { promise: null };
        tarifasRequestRef.current = request;
        setLoadingTarifas(true);
        request.promise = (async () => {
            try {
                const res = await authFetch('/api/sale/tarifas');
                if (tarifasRequestRef.current !== request) return;
                if (!res.ok) {
                    console.error(`[SalesDataProvider] Error fetching tariffs: HTTP ${res.status}`);
                    return;
                }

                const data = await res.json();
                if (tarifasRequestRef.current !== request) return;
                setTarifas(data.tarifas || data.content || []);
                tarifasLoadedRef.current = true;
            } catch (err) {
                if (tarifasRequestRef.current === request) {
                    console.error("DEBUG [SalesDataProvider] Error fetching tariffs:", err);
                }
            } finally {
                if (tarifasRequestRef.current === request) {
                    tarifasRequestRef.current = null;
                    setLoadingTarifas(false);
                }
            }
        })();
        return request.promise;
    }, [authFetch]);

    useEffect(() => {
        tarifasLoadedRef.current = false;
        tarifasRequestRef.current = null;
        setTarifas([]);
        setLoadingTarifas(false);
    }, [authFetch]);

    useEffect(() => {
        // Realtime subscription - escuchar por tramite_id o contact_id
        if (!tramiteId && !contactId) return;
        const channelName = tramiteId 
            ? `sales-data-tramite-${tramiteId}` 
            : `sales-data-contact-${contactId}`;
        
        const filter = tramiteId 
            ? `tramite_id=eq.${tramiteId}`
            : `contact_id=eq.${contactId}`;

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'espuma',
                    table: 'sales_forms',
                    filter: filter
                },
                (payload) => {
                    console.log('[SalesDataProvider] Realtime update:', payload);
                    if (payload.new) {
                        if (payload.new.form_data) distributeData(payload.new.form_data);
                        if (payload.new.status) setFormStatus(payload.new.status);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
            // Also reset field sources when tramite changes or unmounts
            setFieldSources({});
        };
    }, [tramiteId, contactId, authFetch, distributeData]);


    const value = {
        sectionData,
        fieldSources,
        formStatus,
        setFormStatus, // Allow manual override if needed
        offers,
        loadingOffers,
        tarifas,
        loadingTarifas,
        loadTarifas,
        // expose helper to update any section
        updateSectionData,
        // lead helpers / state
        fetchLeadByPhone,
        leadLoading,
        leadSearchId,
        // New global states for extraction/price
        priceData,
        setPriceData,
        extractedData,
        setExtractedData,
        analyzedAttachments,
        setAnalyzedAttachments,
        scoringError,
        setScoringError,
        comparatorError,
        setComparatorError
    };

    return (
        <SalesDataContext.Provider value={value}>
            {children}
        </SalesDataContext.Provider>
    );
};
